// In App navigatio component to create modals

import { Injectable } from '@angular/core';
import { AlertController, MenuController, ModalController, Platform, PopoverController } from '@ionic/angular';
import { checkRoutesName, stringToClass } from './factory';
import { PopoverClass } from 'src/app/popovers/popover-factory';
import { NavigationOptions } from 'src/app/interfaces/navigation-interface'
import { Router } from '@angular/router';
import { ScreenOrientation } from "@ionic-native/screen-orientation/ngx";

import type { PopoverSize } from '@ionic/core';
import { EventsService } from 'src/app/services/event/event.service';

@Injectable({
	providedIn: 'root',
})

export class Navigator {

	public activePage: any = '';
	public activeModals: HTMLIonModalElement[] = [];
	public activePopovers: HTMLIonPopoverElement[] = [];
	public activePriorityPopovers: HTMLIonPopoverElement[] = [];
	public currentTab: string;
	public modal: any;
	public isMobileApp: boolean = false;

	constructor(
		public alertController: AlertController,
		public events: EventsService,
		public menuCtrl: MenuController,
		public modalCtrl: ModalController,
		public platform: Platform,
		public popoverController: PopoverController,
		public router: Router,
		public screenOrientation: ScreenOrientation,
	) {

	}

	/*
	passing pagename, options and params to pass data from one page to another
	to get the params data in page use it like this.....
	any variable defined inside params object can be accessed using @Input()
	See DepositSuccessFailureComponent for Example
	*/
	public navigateTo(pageName, options?: any, params?: any, menuSide?) {
		let page = stringToClass(pageName);
		let pageParams = (typeof params !== "undefined") ? params : "";
		let ModalOptions: NavigationOptions = this.getOptions(options);

		//get the modal route with friendly path 
		let modalRoute = checkRoutesName(pageName);

		if (this.activeModals.length && !ModalOptions.multipleModals) {
			this.dismissAll();
		}
		//to avoid the long time of dismissAll to dismiss the new modal that we want to open in some cases
		setTimeout(() => {
			this.openModal(page, pageName, ModalOptions, pageParams, menuSide).then(() => this.sendHistoryEvent(modalRoute));
		}, 500);
	}

	async openModal(page: any, pageName, ModalOptions?: any, params?: any, menuSide?) {

		const backdropDismissFlag: boolean = (typeof ModalOptions.backdropDismiss === "undefined") ? false : ModalOptions.backdropDismiss;
		const customClass: string = (typeof ModalOptions.customModalClass === "undefined") ? 'custom-modal-class' : ModalOptions.customModalClass;
		const animated: boolean = (typeof ModalOptions.animated === "undefined") ? false : ModalOptions.animated;
		const enterAnimation: any = (typeof ModalOptions.enterAnimation === "undefined") ? null : ModalOptions.enterAnimation;

		const modal = await this.modalCtrl.create({
			component: page,
			componentProps: params,
			cssClass: customClass,
			animated: animated,
			backdropDismiss: backdropDismissFlag,
			enterAnimation: enterAnimation
		});
		this.activeModals.push(modal);

		this.activePage = pageName;

		return await modal.present();
	}

	private getOptions(options: NavigationOptions) {
		let ModalOptions: NavigationOptions = {};
		if (typeof options === "undefined") {
			return ModalOptions = {
				multipleModals: false,
				backdropDismiss: false,
				customModalClass: 'custom-modal-class',
				callback: null
			}
		}

		ModalOptions.multipleModals = (typeof options.multipleModals === "undefined") ? false : options.multipleModals;
		ModalOptions.backdropDismiss = (typeof options.backdropDismiss === "undefined") ? false : options.backdropDismiss;
		ModalOptions.customModalClass = (typeof options.customModalClass === "undefined") ? 'custom-modal-class' : options.customModalClass;
		ModalOptions.callback = (typeof options.callback === "undefined") ? null : options.callback;
		ModalOptions.animated = (typeof options.animated === "undefined") ? false : options.enterAnimation;
		ModalOptions.enterAnimation = (typeof options.enterAnimation === "undefined") ? null : options.enterAnimation;
		return ModalOptions;
	}

	readDeviceOrientation() {
		if (Math.abs(window.orientation) === 90) {
			// Landscape
			return "portrait-landscape";
		} else {
			// Portrait
			return "portrait-primary";
		}
	}

	async openPopover(
		popoverName: string = '',
		data: any = {},
		popoverStyle: string = '',
		backdropDismiss: boolean = true,
		animated: boolean = true,
		showBackdrop: boolean = true,
		closeTimer: number = -1,
		size: PopoverSize = 'cover', // by default ion-popver uses 'auto', but auto' is not good for here,
		onDefaultCenter: boolean = true,
		priorityPopover: boolean = false
	) {
		if (!popoverName) {
			console.log(`popover name cannot be blank -- ${popoverName}`);
			return;
		}

		this.isMobileApp = this.platform.is('capacitor');
		const popoverPage = PopoverClass(popoverName);

		if (this.isMobileApp || this.platform.is('android') || this.platform.is('ios') || this.platform.is('ipad')) {
			if (onDefaultCenter) {
				if (this.platform.is('ipad') && (popoverStyle.includes("game-history-details") || popoverStyle.includes("self-exclude"))) {
					popoverStyle += this.readDeviceOrientation() == 'portrait-primary' ? " game-history-details-and-self-exclude-portait" : " game-history-details-and-self-exclude-landscape";
				} else if (this.isMobileApp) {
					popoverStyle += this.readDeviceOrientation() == 'portrait-primary' ? " center-popover-from-portait-app" : " center-popover-from-landscape-app";
				} else {
					if (!this.platform.is('ios')) {
						popoverStyle += this.readDeviceOrientation() == 'portrait-primary' ? " center-popover-from-portait-app" : " center-popover-from-landscape-app";
					} else {
						popoverStyle += this.readDeviceOrientation() == 'portrait-primary' ? " center-popover-from-portait-web" : " center-popover-from-landscape-web";
					}
				}
			}
		}

		if (!popoverPage) {
			return;
		}

		return await this.popoverController
			.create({
				component: popoverPage,
				componentProps: { data },
				animated,
				showBackdrop,
				backdropDismiss,
				cssClass: popoverStyle,
				size,
			}).then(popover => {
				this.activePopovers.push(popover);
				if (priorityPopover) {
					this.activePriorityPopovers.push(popover)
				}
				popover.present();
				if (closeTimer > 0) {
					setTimeout(() => {
						popover.dismiss();
					}, closeTimer)
				}
				return popover;
			});
	}

	public dismiss() {
		const modal = this.activeModals.pop();
		modal?.dismiss({
			dismissed: true,
		});
		return this.activeModals.length > 0;
	}

	public dismissPriorityPopover() {
		if (this.activePriorityPopovers.length > 0) {
			this.activePriorityPopovers.pop();
			if (this.activePopovers.length > 0) {
				this.activePopovers.pop();
			}
		}
	}

	public dismissPopover() {
		if (this.activePopovers.length > 0) {
			const popover = this.activePopovers.pop();
			if (popover) {
				popover.dismiss();
			}
		}
	}

	public dismissAll() {
		this.activeModals.forEach(modal => {
			modal.dismiss();
		});

		while (this.activePopovers.length > 0) {
			const popover = this.activePopovers.pop();
			popover.dismiss();
		}

		this.activeModals = [];
		this.activePopovers = [];
		this.activePriorityPopovers = [];

		const popoverController = this.popoverController;
		popoverController.getTop()
			.then(popover => {
				if (popover) {
					popoverController.dismiss();
				}
			});

		const alertController = this.alertController;
		alertController.getTop()
			.then(alert => {
				if (alert) {
					alertController.dismiss();
				}
			});

		return;
	}

	public back() {
		if (this.activeModals.length) {
			this.closeActiveModal();
		} else {
			this.modalCtrl.dismiss();
		}
		//temporary removing back call for page history
		//this.sendHistoryEvent();
	}

	private closeActiveModal() {
		let modal = this.activeModals[this.activeModals.length - 1];
		modal.dismiss();
		this.activeModals.pop();
	}

	public getActivePage() {
		return this.activePage;
	}

	public pushHistoryState(pathname) {
		let newurl = window.location.protocol + "//" + window.location.host + '/' + pathname + window.location.search + document.location.hash;
		history.pushState({ path: newurl }, '', newurl);
		this.events.publish("event:history:push", { url: newurl });
	}

	public toTab(tabname: string, dismissModals?: boolean) {
		const tabname_lc = tabname?.toLowerCase();
		this.currentTab = tabname_lc;
		if (dismissModals) setTimeout(() => this.dismissAll(), 50); //to close all modals before navigating to the tab. 
		return this.router.navigate([tabname_lc]).then(() => this.sendHistoryEvent(tabname_lc));
	}

	public redirectToCasinoNoHistory(): void {
		this.router.navigate(['/casino'], { replaceUrl: true });
	}

	public toWebview(dismissModals?: boolean) {
		return this.toTab('c', dismissModals);
	}

	public toCasino(dismissModals?: boolean) {
		return this.toTab('casino', dismissModals);
	}

	public toGametab(dismissModals?: boolean) {
		return this.toTab('launcher', dismissModals);
	}

	public toHome(dismissModals?: boolean) {
		return this.toTab('home', dismissModals);
	}

	public toSplashPage(dismissModals?: boolean) {
		return this.toTab('landing', dismissModals);
	}

	private sendHistoryEvent(componentPath) {
		this.events.publish("event:history:push", { page: componentPath })
	}

	public toLobby() {
		return this.toCasino(true);
	}

	public onReloadApp() {
		const origin: string = window.location.origin;
		window.location.href = origin;
		return setTimeout(() => window.location.reload(), 100);
	}

	public goBackOrExitApp() {
		if (this.activeModals && this.activeModals.length > 0) {
			this.closeActiveModal();
		} else {
			navigator['app'].exitApp();
		}
	}
}
